import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  getRulesetTypesByCustomer, 
  createRulesetType, 
  updateRulesetType, 
  deleteRulesetType,
  checkRulesetNameExists 
} from '../services/api/rulesetApi';

/**
 * Custom hook for managing rule sets for a specific customer
 * @param {string} customerId - The ID of the customer
 * @returns {Object} Rule sets state and CRUD operations
 */
export const useRuleSets = (customerId) => {
  const [rulesets, setRulesets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use ref to prevent duplicate API calls in StrictMode
  const hasFetched = useRef(false);
  const currentCustomerId = useRef(customerId);

  useEffect(() => {
    // Reset when customerId changes
    if (currentCustomerId.current !== customerId) {
      currentCustomerId.current = customerId;
      hasFetched.current = false;
      setRulesets([]);
    }

    if (!hasFetched.current && customerId) {
      hasFetched.current = true;
      fetchRulesets();
    }
  }, [customerId]);

  const fetchRulesets = async () => {
    if (!customerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getRulesetTypesByCustomer(customerId);
      setRulesets(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch rule sets');
      console.error('Error fetching rule sets:', err);
    } finally {
      setLoading(false);
    }
  };

  const createRuleset = async (rulesetData) => {
    try {
      setError(null);

      // Validate rule set name uniqueness
      const checkResponse = await checkRulesetNameExists(customerId, rulesetData.name);
      if (checkResponse.data === true) {
        throw new Error('A rule set with this name already exists for this customer');
      }

      // Automatically associate with customer ID
      const dataWithCustomer = {
        ...rulesetData,
        customerId: customerId
      };

      const response = await createRulesetType(dataWithCustomer);
      
      // Refresh the list
      await fetchRulesets();
      
      return response.data;
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to create rule set';
      setError(errorMessage);
      console.error('Error creating rule set:', err);
      throw err;
    }
  };

  const updateRuleset = async (id, rulesetData) => {
    try {
      setError(null);

      // Validate rule set name uniqueness (excluding current rule set)
      if (rulesetData.name) {
        const checkResponse = await checkRulesetNameExists(customerId, rulesetData.name, id);
        if (checkResponse.data === true) {
          throw new Error('A rule set with this name already exists for this customer');
        }
      }

      const response = await updateRulesetType(id, rulesetData);
      
      // Refresh the list
      await fetchRulesets();
      
      return response.data;
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to update rule set';
      setError(errorMessage);
      console.error('Error updating rule set:', err);
      throw err;
    }
  };

  const deleteRuleset = async (id) => {
    try {
      setError(null);

      await deleteRulesetType(id);
      
      // Refresh the list
      await fetchRulesets();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete rule set');
      console.error('Error deleting rule set:', err);
      throw err;
    }
  };

  const refreshRulesets = useCallback(async () => {
    hasFetched.current = false;
    await fetchRulesets();
  }, [customerId]);

  return {
    rulesets,
    loading,
    error,
    createRuleset,
    updateRuleset,
    deleteRuleset,
    refreshRulesets
  };
};
